<script lang="ts">
import { page } from '$app/state';
import { sanitizeRichTextHtml } from '$lib/utils/sanitise.js';
import { stripHtmlForSchema } from '$lib/utils/string.js';
import { prepareHtmlLinks } from '$lib/utils/url.js';
import { ETitleFontSizes } from '../../../config/enums.js';

// Module-level counter for SSR-safe unique IDs
let uid = 0;

interface FAQItem {
    question: string;
    answer: string;
}

interface Props {
    showHeading?: boolean;
    heading?: string;
    headingLevel?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
    titleSize?: string;
    items?: FAQItem[];
    allowMultipleOpen?: boolean;
    /** Output FAQPage schema; disable when page has multiple FAQ sections */
    enableSchema?: boolean;
    hideBlock?: boolean;
}

let {
    showHeading = true,
    heading = 'Frequently Asked Questions',
    headingLevel = 'h2',
    titleSize = ETitleFontSizes.H4,
    items = [],
    allowMultipleOpen = true,
    enableSchema = true,
    hideBlock = false,
}: Props = $props();

// Generate CSS class name for title size
let titleSizeClassName = $derived(`faq__heading--${titleSize}`);

// State to track which FAQ is currently open (for accordion mode)
let openIndex = $state<number | null>(null);

// Generate unique ID for ARIA (SSR-safe using module-level counter)
const sectionId = `faq-${++uid}`;
const headingId = `${sectionId}-heading`;

// Auto-extract clean page URL (origin + pathname, no query params)
let pageUrl = $derived(page.url.origin + page.url.pathname);

// Generate FAQPage schema.org structured data
let faqSchema = $derived.by(() => {
    if (!enableSchema || !items?.length) return null;
    return {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        '@id': `${pageUrl}#faq`,
        mainEntity: items.map((item) => ({
            '@type': 'Question',
            name: item.question,
            acceptedAnswer: {
                '@type': 'Answer',
                text: stripHtmlForSchema(item.answer),
            },
        })),
    };
});

function handleToggle(index: number, event: Event) {
    if (!allowMultipleOpen) {
        const detailsElement = event.target as HTMLDetailsElement;
        const isOpening = detailsElement.open;

        if (isOpening) {
            // Close other open items
            if (openIndex !== null && openIndex !== index) {
                const previousDetails = document.querySelector(
                    `.faq__item:nth-child(${openIndex + 1}) details`,
                ) as HTMLDetailsElement;
                if (previousDetails) {
                    previousDetails.open = false;
                }
            }
            openIndex = index;
        } else {
            openIndex = null;
        }
    }
}
</script>

<svelte:head>
    {#if faqSchema}
        {@html `<script type="application/ld+json">${JSON.stringify(faqSchema).replace(/<\/script/gi, '<\\/script')}</script>`}
    {/if}
</svelte:head>

{#if !hideBlock}
<section class="faq builder-text" aria-labelledby={showHeading ? headingId : undefined}>
	{#if showHeading && heading}
		<svelte:element this={headingLevel} id={headingId} class="faq__heading {titleSizeClassName}">
			{heading}
		</svelte:element>
	{/if}

	{#if items && items.length > 0}
		<div class="faq__list">
			{#each items as item, index (item.question)}
				<div class="faq__item">
					<details ontoggle={(e) => handleToggle(index, e)}>
						<summary class="faq__summary">
							<span class="faq__question" id="{sectionId}-question-{index}">{item.question}</span>
							<span class="faq__icon" aria-hidden="true"></span>
						</summary>
						<div
							class="faq__content builder-text"
							id="{sectionId}-content-{index}"
							role="region"
							aria-labelledby="{sectionId}-question-{index}"
						>
							{@html prepareHtmlLinks(sanitizeRichTextHtml(item.answer))}
						</div>
					</details>
				</div>
			{/each}
		</div>
	{/if}
</section>
{/if}

<style lang="scss">
	@use '/src/scss/global-constants/typography';
	@use 'sass-mq/mq' as *;

	.faq {
		&__heading {
			color: var(--colour-dreamless-sleep);
			font-size: 2.2rem;
			font-weight: 400;
			letter-spacing: -0.066rem;
			line-height: 1.4;
			margin-bottom: 2.6rem;
			text-align: left;

			@include mq($from: tablet) {
				margin-bottom: 2.6rem;
			}

			&--h1 {
				@include typography.display-xl;
			}

			&--h1-5 {
				@include typography.display-hero;
			}

			&--h2 {
				@include typography.display-lg;
			}

			&--h3 {
				@include typography.display-md;
			}

			&--h4 {
				@include typography.heading-lg;
			}

			&--h5 {
				@include typography.heading-md;
			}
		}

		&__list {
			display: flex;
			flex-direction: column;
			gap: 0;
		}

		&__item {
			border-bottom: 1px solid var(--colour-jupiter);

			&:first-child {
				border-top: 1px solid var(--colour-jupiter);
			}

			details {
				summary {
					list-style: none;
					cursor: pointer;

					&::-webkit-details-marker {
						display: none;
					}
				}

				// Smooth animation for opening/closing
				&[open] {
					.faq__content {
						animation: slideDown 0.3s ease-out;
					}

					.faq__icon::before {
						transform: rotate(90deg);
					}
				}
			}
		}

		&__summary {
			display: flex;
			align-items: center;
			justify-content: space-between;
			gap: 1.6rem;
			padding: 1.5rem 0;
			color: var(--colour-dreamless-sleep);
			font-family: var(--font-secondary);
			font-size: 1.6rem;
			font-weight: 600;
			letter-spacing: 0.032rem;
			line-height: 1.2;

			@include mq($from: tablet) {
				padding: 1.5rem 0;
			}

			&:focus-visible {
				outline: 2px solid var(--colour-squant);
				outline-offset: 2px;
			}
		}

		&__question {
			flex: 1;
		}

		&__icon {
			flex-shrink: 0;
			align-items: center;
			display: flex;
			height: 1.0814rem;
			justify-content: center;
			width: 0.9894rem;

			&::before {
				background: url('/src/stories/assets/faq-arrow.svg') no-repeat center;
				background-size: 100% 100%;
				content: '';
				display: block;
				flex: 0 0 1.0814rem;
				height: 0.9894rem;
				transform: rotate(-90deg);
				transition: transform 0.3s ease;
				width: 1.0814rem;
			}
		}

		&__content {
			color: var(--colour-dreamless-sleep);
			font-size: 1.6rem;
			line-height: 1.4;
			max-width: none;
			padding: 0 0 1.5rem 0;

			@include mq($from: tablet) {
				padding: 0 0 1.5rem 0;
			}

			// Rich text styling provided by .builder-text class
		}
	}

	// Smooth slide down animation
	@keyframes slideDown {
		from {
			opacity: 0;
			transform: translateY(-1rem);
		}
		to {
			opacity: 1;
			transform: translateY(0);
		}
	}

	// Respect user's motion preferences
	@media (prefers-reduced-motion: reduce) {
		.faq__item details[open] .faq__content {
			animation: none;
		}

		.faq__icon {
			&::before {
				transition: none;
			}
		}
	}
</style>
